import { getRouteIdFromPathname, routes, RouteId } from '@trello/routes';

import { useWorkspaceForBoardQuery } from './WorkspaceForBoard.generated';
import { useWorkspaceForCardQuery } from './WorkspaceForCard.generated';
import { useWorkspaceForOrganizationQuery } from './WorkspaceForOrganization.generated';
import { WorkspaceState } from './workspaceState';

interface RouteState {
  type: 'BOARD' | 'CARD' | 'ORGANIZATION' | 'GLOBAL';
  idBoard?: string;
  idCard?: string;
  organizationName?: string;
}

interface RouteStateGetter {
  (...args: string[]): RouteState;
}

function getBoardRouteState(idBoard: string): RouteState {
  return { type: 'BOARD', idBoard };
}

function getCardRouteState(idCard: string): RouteState {
  return { type: 'CARD', idCard };
}

function getOrganizationRouteState(organizationName: string): RouteState {
  return { type: 'ORGANIZATION', organizationName };
}

function getGlobalRouteState(): RouteState {
  return { type: 'GLOBAL' };
}

export const routeMap: Record<RouteId, RouteStateGetter> = {
  [RouteId.BOARD]: getBoardRouteState,
  [RouteId.BOARD_DASHBOARD]: getBoardRouteState,

  [RouteId.BOARD_OLD]: (path: string) => {
    const parts = path.split('/');
    const idBoard = parts.length > 1 ? parts[1] : parts[0];
    return getBoardRouteState(idBoard);
  },

  [RouteId.BOARD_REFERRAL]: getBoardRouteState,

  [RouteId.CARD_OLD]: (...params: string[]) => {
    const idBoard = params.length > 2 ? params[1] : params[0];
    return getBoardRouteState(idBoard);
  },

  [RouteId.CARD_AND_BOARD_OLD]: (...params: string[]) => {
    const idBoard = params.length > 2 ? params[1] : params[0];
    return getBoardRouteState(idBoard);
  },

  [RouteId.CARD]: getCardRouteState,

  [RouteId.MEMBER_CARDS_FOR_ORG]: getOrganizationRouteState,
  [RouteId.MEMBER_HOME_BOARDS]: getOrganizationRouteState,
  [RouteId.ORGANIZATION_BY_ID]: getOrganizationRouteState,
  [RouteId.ORGANIZATION_EXPORT]: getOrganizationRouteState,
  [RouteId.ORGANIZATION_FREE_TRIAL]: getOrganizationRouteState,
  [RouteId.ORGANIZATION_GUESTS]: getOrganizationRouteState,
  [RouteId.ORGANIZATION_MEMBER_CARDS]: getOrganizationRouteState,
  [RouteId.ORGANIZATION_MEMBERS]: getOrganizationRouteState,
  [RouteId.ORGANIZATION_POWER_UPS]: getOrganizationRouteState,
  [RouteId.ORGANIZATION_TABLES]: getOrganizationRouteState,
  [RouteId.PROFILE]: getOrganizationRouteState,
  [RouteId.PROFILE]: getOrganizationRouteState,
  [RouteId.TEAM_GETTING_STARTED]: getOrganizationRouteState,
  [RouteId.TEAM_HIGHLIGHTS]: getOrganizationRouteState,
  [RouteId.TEAM_REPORTS]: getOrganizationRouteState,
  [RouteId.USER_OR_ORG]: getOrganizationRouteState,

  [RouteId.ACCOUNT]: getGlobalRouteState,
  [RouteId.BILLING_CARD_PRODUCT]: getGlobalRouteState,
  [RouteId.BILLING_CARD]: getGlobalRouteState,
  [RouteId.BILLING]: getGlobalRouteState,
  [RouteId.BLANK]: getGlobalRouteState,
  [RouteId.CREATE_FIRST_BOARD]: getGlobalRouteState,
  [RouteId.CREATE_FIRST_TEAM]: getGlobalRouteState,
  [RouteId.DOUBLE_SLASH]: getGlobalRouteState,
  [RouteId.ENTERPRISE_ADMIN_TAB]: getGlobalRouteState,
  [RouteId.ENTERPRISE_ADMIN]: getGlobalRouteState,
  [RouteId.ERROR_PAGE]: getGlobalRouteState,
  [RouteId.GO]: getGlobalRouteState,
  [RouteId.INVITE_ACCEPT_BOARD]: getGlobalRouteState,
  [RouteId.INVITE_ACCEPT_TEAM]: getGlobalRouteState,
  [RouteId.MEMBER_ACTIVITY]: getGlobalRouteState,
  [RouteId.MEMBER_ALL_BOARDS]: getGlobalRouteState,
  [RouteId.MEMBER_CARDS]: getGlobalRouteState,
  [RouteId.MEMBER_HOME]: getGlobalRouteState,
  [RouteId.MEMBER_TASKS]: getGlobalRouteState,
  [RouteId.POWER_UP_ADMIN]: getGlobalRouteState,
  [RouteId.POWER_UP_EDIT]: getGlobalRouteState,
  [RouteId.POWER_UP_PUBLIC_DIRECTORY]: getGlobalRouteState,
  [RouteId.SEARCH]: getGlobalRouteState,
  [RouteId.SELECT_ORG_TO_UPGRADE]: getGlobalRouteState,
  [RouteId.SELECT_TEAM_TO_UPGRADE]: getGlobalRouteState,
  [RouteId.SHORTCUTS_OVERLAY]: getGlobalRouteState,
  [RouteId.SHORTCUTS]: getGlobalRouteState,
  [RouteId.TEMPLATES_RECOMMEND]: getGlobalRouteState,
  [RouteId.TEMPLATES_SUBMIT]: getGlobalRouteState,
  [RouteId.TEMPLATES]: getGlobalRouteState,
  [RouteId.TO]: getGlobalRouteState,
  [RouteId.WELCOME]: getGlobalRouteState,
};

export function useWorkspaceForPathname(pathname: string): WorkspaceState {
  const routeId = getRouteIdFromPathname(pathname);
  const getter = routeMap[routeId];
  const match = routes[routeId].regExp.exec(pathname.slice(1));
  const params = match?.slice(1) ?? [];

  const { type, idBoard, idCard, organizationName } = getter(...params);

  const { data: boardData, loading: boardLoading } = useWorkspaceForBoardQuery({
    variables: {
      idBoard: idBoard ?? '',
    },
    skip: type !== 'BOARD',
    // This property is required to prevent the hook from getting stuck in a loading state
    // for more detail: https://hello.atlassian.net/wiki/spaces/TRELLO/pages/1006320263/Bug+in+apollo+react-hooks+useQuery+stuck+in+loading+state
    // TODO: Try to remove this after FE-Plat upgrades to @apollo/client
    notifyOnNetworkStatusChange: true,
  });

  const { data: cardData, loading: cardLoading } = useWorkspaceForCardQuery({
    variables: {
      idCard: idCard ?? '',
    },
    skip: type !== 'CARD',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: organizationData,
    loading: organizationLoading,
  } = useWorkspaceForOrganizationQuery({
    variables: {
      name: organizationName ?? '',
    },
    skip: type !== 'ORGANIZATION',
    notifyOnNetworkStatusChange: true,
  });

  let isGlobal = type === 'GLOBAL';
  let idWorkspace: string | null, isLoading: boolean;
  switch (type) {
    case 'BOARD':
      idWorkspace = boardData?.board?.idOrganization ?? null;
      isLoading = boardLoading;
      break;
    case 'CARD':
      idWorkspace = cardData?.card?.board?.idOrganization ?? null;
      isLoading = cardLoading;
      break;
    case 'ORGANIZATION':
      idWorkspace = organizationData?.organization?.id ?? null;
      isLoading = organizationLoading;

      // If there is no idWorkspace on the USER_OR_ORG route then it means we
      // are on the USER route which should be a global page
      if (!isLoading && routeId === RouteId.USER_OR_ORG && !idWorkspace) {
        isGlobal = true;
      }

      break;
    case 'GLOBAL':
    default:
      idWorkspace = null;
      isLoading = false;
      break;
  }

  return { idWorkspace, isLoading, isGlobal };
}
